import React, { useState } from "react";
import { motion } from "framer-motion";
import { fadeIn, staggerContainer } from "../variants";
import { contactData } from "../data";
import { Send, CheckCircle } from "lucide-react";

const Contact = () => {
  const { eyebrow, title, info } = contactData;
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleChange = (e) =>
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const handleSubmit = (e) => {
    e.preventDefault();
    setLoading(true);
    const mailto = `mailto:association.appe@yahoo.com?subject=${encodeURIComponent(
      form.subject || "Message depuis le site APPE"
    )}&body=${encodeURIComponent(
      `Nom: ${form.name}\nEmail: ${form.email}\n\n${form.message}`
    )}`;
    window.location.href = mailto;
    setTimeout(() => {
      setSent(true);
      setLoading(false);
    }, 800);
  };

  return (
    <section id="contact" className="section scroll-mt-20 bg-light">
      <div className="container mx-auto">

        {/* Header */}
        <motion.div
          variants={staggerContainer(0.12, 0.1)}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.15 }}
          className="section-header"
        >
          <motion.p variants={fadeIn("up")} className="eyebrow mb-4">
            {eyebrow}
          </motion.p>
          <motion.h2 variants={fadeIn("up", 0.05)} className="h2 mb-6">
            {title}
          </motion.h2>
          <motion.p variants={fadeIn("up", 0.1)} className="lead">
            Une question, un projet de partenariat, une envie de nous rejoindre en tant que bénévole ?
            Écrivez-nous, nous vous répondons dans les meilleurs délais.
          </motion.p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-12">

          {/* Contact info */}
          <motion.div
            variants={fadeIn("right")}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.15 }}
            className="space-y-6"
          >
            {info.map((item, i) => (
              <div key={i} className="card p-6">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center flex-shrink-0">
                    <span className="text-primary text-base font-bold">{i + 1}</span>
                  </div>
                  <div className="flex-1">
                    <h3 className="font-heading font-bold text-dark text-base mb-1">{item.title}</h3>
                    <p className="text-secondary text-xs font-medium mb-4">{item.subtitle}</p>

                    <div className="space-y-3">
                      <div className="flex items-start gap-3">
                        <span className="text-primary mt-0.5 flex-shrink-0">{item.address.icon}</span>
                        <span className="text-grey text-sm">{item.address.name}</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-primary flex-shrink-0">{item.phone.icon}</span>
                        <a href={`tel:${item.phone.number.replace(/\s/g, "")}`} className="text-dark text-sm font-medium hover:text-primary transition-colors">
                          {item.phone.number}
                        </a>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-primary flex-shrink-0">{item.email.icon}</span>
                        <a href={`mailto:${item.email.address}`} className="text-dark text-sm font-medium hover:text-primary transition-colors break-all">
                          {item.email.address}
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}

            {/* Social links */}
            <div className="card p-6">
              <h4 className="font-heading font-bold text-dark text-base mb-4">Nous suivre</h4>
              <div className="flex gap-3">
                <a
                  href="https://www.facebook.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 bg-blue-50 text-blue-600 hover:bg-blue-100 font-medium text-sm px-4 py-2.5 rounded-lg transition-colors"
                >
                  Facebook
                </a>
              </div>
            </div>
          </motion.div>

          {/* Form */}
          <motion.div
            variants={fadeIn("left")}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.15 }}
          >
            <div className="card p-8">
              {sent ? (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <CheckCircle size={48} className="text-accent mb-4" />
                  <h3 className="font-heading font-bold text-xl text-dark mb-2">Message envoyé !</h3>
                  <p className="text-grey text-sm">Votre client mail s'est ouvert. Merci de votre message, nous vous répondrons bientôt.</p>
                  <button onClick={() => setSent(false)} className="mt-6 text-primary font-medium text-sm hover:underline">
                    Envoyer un autre message
                  </button>
                </div>
              ) : (
                <>
                  <h3 className="font-heading font-bold text-xl text-dark mb-6">Envoyez-nous un message</h3>
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-semibold text-dark mb-1.5 uppercase tracking-wide">
                          Nom complet *
                        </label>
                        <input
                          type="text"
                          name="name"
                          required
                          value={form.name}
                          onChange={handleChange}
                          placeholder="Jean Dupont"
                          className="w-full px-4 py-3 rounded-xl border border-gray-200 text-dark text-sm placeholder:text-grey/50 outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 transition-all bg-light"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-dark mb-1.5 uppercase tracking-wide">
                          Email *
                        </label>
                        <input
                          type="email"
                          name="email"
                          required
                          value={form.email}
                          onChange={handleChange}
                          placeholder="jean@example.com"
                          className="w-full px-4 py-3 rounded-xl border border-gray-200 text-dark text-sm placeholder:text-grey/50 outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 transition-all bg-light"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-dark mb-1.5 uppercase tracking-wide">
                        Objet
                      </label>
                      <input
                        type="text"
                        name="subject"
                        value={form.subject}
                        onChange={handleChange}
                        placeholder="Ex: Demande de partenariat"
                        className="w-full px-4 py-3 rounded-xl border border-gray-200 text-dark text-sm placeholder:text-grey/50 outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 transition-all bg-light"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-dark mb-1.5 uppercase tracking-wide">
                        Message *
                      </label>
                      <textarea
                        name="message"
                        required
                        rows={5}
                        value={form.message}
                        onChange={handleChange}
                        placeholder="Dites-nous comment nous pouvons collaborer..."
                        className="w-full px-4 py-3 rounded-xl border border-gray-200 text-dark text-sm placeholder:text-grey/50 outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 transition-all bg-light resize-none"
                      />
                    </div>
                    <button
                      type="submit"
                      disabled={loading}
                      className="w-full flex items-center justify-center gap-3 bg-primary hover:bg-primary-dark text-white font-semibold py-4 rounded-xl transition-all duration-300 shadow-md hover:shadow-lg disabled:opacity-70"
                    >
                      {loading ? (
                        <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      ) : (
                        <Send size={16} />
                      )}
                      {loading ? "Ouverture du client mail..." : "Envoyer le message"}
                    </button>
                  </form>
                </>
              )}
            </div>
          </motion.div>
        </div>

      </div>
    </section>
  );
};

export default Contact;
